// `?debug=solids` (§28, WP6 alignment check): a wireframe of the sim's solids and walkable
// boundaries over the rendered map, always on top. Boundaries (green) are traced from `groundAt`
// (marching squares, crossings refined by bisection) and re-traced when the map state they depend
// on changes (tiles, wedges, tilt). Solids are re-read from `MapDef.solids` every frame: static ones
// in blue, moving ones in pink, each outlined at its top and at the arena floor.
// Pose fidelity (§29.4 WP6): the axes of the hazards as the map view *drew* them (yellow, with a
// cross at each end) sit on top of the sim capsules, so any drift between render and sim shows at
// a glance; and the hazards that are not solids are drawn as zones from the replicated state and
// the pose (orange): candle flame reach while a candle flickers or fires, bubble pop radius while a
// bubble wobbles, warned and fallen teacup rim wedges, cracking cake tiles.
import { BufferAttribute, BufferGeometry, Color, DynamicDrawUsage, Group, LineBasicMaterial, LineSegments, type Object3D, type Vector3 } from 'three';
import { VOID } from '@shared/constants';
import { MapId, TileState } from '@shared/ids';
import { CAKE_GRID, CANDLE_TILES, TUNING as CAKE } from '@shared/maps/cake';
import type { Solid } from '@shared/maps/mapTypes';
import { TUNING as SPIN } from '@shared/maps/spincycle';
import { TUNING as TEACUP } from '@shared/maps/teacup';
import type { FrameView } from '../../game/FrameView';
import { mapDebug } from './debug/state';
import type { MapProbe } from './probe';

/** A hazard as the map view drew it: two world points (three.js) on its axis. */
export interface RenderedHazard { kind: string; a: Vector3; b: Vector3; radius: number }

export interface SolidsOverlay {
  readonly root: Object3D;
  /** The map of the round just built; its probe answers ground and solid queries. */
  setRound(probe: MapProbe): void;
  /** After the probe has been synced to `view`. */
  update(view: FrameView): void;
  /** Replaces `MapDef.solids` as the source of the drawn solids (the WP6 page's synthetic hazards); null restores it. */
  setSource(fn: ((view: FrameView, out: Solid[]) => number) | null): void;
  /** The map view's drawn hazards (yellow axes over the sim solids); null: the active map view's. */
  setRendered(fn: (() => readonly RenderedHazard[]) | null): void;
  dispose(): void;
}

const OVERLAY = {
  cellM: 0.1,
  bisectSteps: 8,
  /** Lines float this far above the surface they outline. */
  liftM: 0.03,
  circleSegments: 32,
  /** Initial line buffer capacity, vertices. */
  initialVertices: 4096,
  /** Tilt changes smaller than this (rad) keep the traced boundary. */
  tiltStep: 0.002,
  renderOrder: 1000,
} as const;
const COLORS = {
  boundary: new Color('#00C853'), solid: new Color('#00B8FF'), moving: new Color('#FF2D95'),
  rendered: new Color('#FFE600'), zone: new Color('#FF9100'), gone: new Color('#FF1744'),
} as const;
const HAS_GROUND = VOID / 2;

/** Edge pairs crossed by the boundary for each marching-squares case (corner bit k = corner k inside). */
const CASE_EDGES: readonly (readonly [number, number][])[] = [
  [], [[3, 0]], [[0, 1]], [[3, 1]], [[1, 2]], [[3, 0], [1, 2]], [[0, 2]], [[3, 2]],
  [[2, 3]], [[0, 2]], [[0, 1], [2, 3]], [[1, 2]], [[1, 3]], [[0, 1]], [[3, 0]], [],
];
/** Corners joined by edge k: 0 (x0,y0)–1 (x1,y0), 1–2 (x1,y1), 2–3 (x0,y1), 3–0. */
const EDGE_CORNERS: readonly [number, number][] = [[0, 1], [1, 2], [2, 3], [3, 0]];
const CORNER_DX = [0, 1, 1, 0] as const;
const CORNER_DY = [0, 0, 1, 1] as const;

/** Growable line-segment vertex buffer in sim coordinates, written as three.js positions. */
class Lines {
  readonly geometry = new BufferGeometry();
  private pos = new Float32Array(0);
  private col = new Float32Array(0);
  private n = 0;

  reset(): void {
    this.n = 0;
  }

  /** A segment from (ax, ay, az) to (bx, by, bz), sim coordinates. */
  add(ax: number, ay: number, az: number, bx: number, by: number, bz: number, c: Color): void {
    if ((this.n + 2) * 3 > this.pos.length) this.grow(this.n + 2);
    this.put(ax, ay, az, c);
    this.put(bx, by, bz, c);
  }

  /** Uploads what was added since `reset`. */
  commit(): void {
    const pos = this.geometry.getAttribute('position');
    if (!pos || pos.array !== this.pos) {
      this.geometry.setAttribute('position', new BufferAttribute(this.pos, 3).setUsage(DynamicDrawUsage));
      this.geometry.setAttribute('color', new BufferAttribute(this.col, 3).setUsage(DynamicDrawUsage));
    } else {
      pos.needsUpdate = true;
      this.geometry.getAttribute('color').needsUpdate = true;
    }
    this.geometry.setDrawRange(0, this.n);
  }

  private put(x: number, y: number, z: number, c: Color): void {
    const i = this.n * 3;
    this.pos[i] = x;
    this.pos[i + 1] = z;
    this.pos[i + 2] = y;
    this.col[i] = c.r;
    this.col[i + 1] = c.g;
    this.col[i + 2] = c.b;
    this.n++;
  }

  /** Grows to hold at least `vertices` (a new GPU buffer: `commit` re-creates the attributes). */
  private grow(vertices: number): void {
    const size = 3 * Math.max(vertices, OVERLAY.initialVertices, (this.pos.length / 3) * 2);
    const pos = new Float32Array(size);
    const col = new Float32Array(size);
    pos.set(this.pos);
    col.set(this.col);
    this.pos = pos;
    this.col = col;
  }
}

/** Traces the ground/void boundary of `probe` over its map bounds into `out`. */
function traceBoundary(probe: MapProbe, out: Lines): void {
  const b = probe.def.bounds;
  const c = OVERLAY.cellM;
  const x0 = b.minX - 1;
  const y0 = b.minY - 1;
  const cols = Math.ceil((b.maxX - b.minX + 2) / c);
  const rows = Math.ceil((b.maxY - b.minY + 2) / c);
  const h = new Float64Array((cols + 1) * (rows + 1));
  for (let j = 0; j <= rows; j++) for (let i = 0; i <= cols; i++) h[j * (cols + 1) + i] = probe.groundAt(x0 + i * c, y0 + j * c);

  const cx = [0, 0, 0, 0];
  const cy = [0, 0, 0, 0];
  const ch = [0, 0, 0, 0];
  const px = [0, 0];
  const py = [0, 0];
  const pz = [0, 0];
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) {
      let mask = 0;
      for (let k = 0; k < 4; k++) {
        cx[k] = x0 + (i + CORNER_DX[k]) * c;
        cy[k] = y0 + (j + CORNER_DY[k]) * c;
        ch[k] = h[(j + CORNER_DY[k]) * (cols + 1) + i + CORNER_DX[k]];
        if (ch[k] > HAS_GROUND) mask |= 1 << k;
      }
      for (const pair of CASE_EDGES[mask]) {
        for (let e = 0; e < 2; e++) {
          const [k0, k1] = EDGE_CORNERS[pair[e]];
          const inK = ch[k0] > HAS_GROUND ? k0 : k1;
          const outK = inK === k0 ? k1 : k0;
          // Bisect between the inside and outside corners for the last point with ground.
          let ix = cx[inK];
          let iy = cy[inK];
          let ox = cx[outK];
          let oy = cy[outK];
          let ih = ch[inK];
          for (let s = 0; s < OVERLAY.bisectSteps; s++) {
            const mx = (ix + ox) / 2;
            const my = (iy + oy) / 2;
            const mh = probe.groundAt(mx, my);
            if (mh > HAS_GROUND) {
              ix = mx;
              iy = my;
              ih = mh;
            } else {
              ox = mx;
              oy = my;
            }
          }
          px[e] = ix;
          py[e] = iy;
          pz[e] = ih + OVERLAY.liftM;
        }
        out.add(px[0], py[0], pz[0], px[1], py[1], pz[1], COLORS.boundary);
      }
    }
  }
}

/** A circle of radius `r` around (x, y) at height z. */
function circle(out: Lines, x: number, y: number, r: number, z: number, color: Color): void {
  const n = OVERLAY.circleSegments;
  for (let i = 0; i < n; i++) {
    const a0 = (i / n) * Math.PI * 2;
    const a1 = ((i + 1) / n) * Math.PI * 2;
    out.add(x + Math.cos(a0) * r, y + Math.sin(a0) * r, z, x + Math.cos(a1) * r, y + Math.sin(a1) * r, z, color);
  }
}

/** A capsule outline (two half circles and two sides) at height z. */
function capsule(out: Lines, s: Solid, z: number, color: Color): void {
  const dx = s.bx - s.ax;
  const dy = s.by - s.ay;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len;
  const ny = dx / len;
  const base = Math.atan2(ny, nx);
  const half = OVERLAY.circleSegments / 2;
  for (const [x, y, from] of [[s.ax, s.ay, base], [s.bx, s.by, base + Math.PI]] as const) {
    for (let i = 0; i < half; i++) {
      const a0 = from + (i / half) * Math.PI;
      const a1 = from + ((i + 1) / half) * Math.PI;
      out.add(x + Math.cos(a0) * s.r, y + Math.sin(a0) * s.r, z, x + Math.cos(a1) * s.r, y + Math.sin(a1) * s.r, z, color);
    }
  }
  out.add(s.ax + nx * s.r, s.ay + ny * s.r, z, s.bx + nx * s.r, s.by + ny * s.r, z, color);
  out.add(s.ax - nx * s.r, s.ay - ny * s.r, z, s.bx - nx * s.r, s.by - ny * s.r, z, color);
}

function drawSolid(out: Lines, s: Solid): void {
  const color = s.vx !== 0 || s.vy !== 0 || s.omega !== 0 ? COLORS.moving : COLORS.solid;
  for (const z of [OVERLAY.liftM, s.top + OVERLAY.liftM]) {
    if (s.kind === 0) circle(out, s.ax, s.ay, s.r, z, color);
    else capsule(out, s, z, color);
  }
  out.add(s.ax, s.ay, OVERLAY.liftM, s.ax, s.ay, s.top + OVERLAY.liftM, color);
  if (s.kind === 1) out.add(s.bx, s.by, OVERLAY.liftM, s.bx, s.by, s.top + OVERLAY.liftM, color);
}

/** A rendered hazard's axis (yellow) with a small cross at each end, at its drawn height. */
function drawRendered(out: Lines, h: RenderedHazard): void {
  const c = COLORS.rendered;
  // three.js (x, y, z) → sim (x, z, y).
  const ax = h.a.x;
  const ay = h.a.z;
  const bx = h.b.x;
  const by = h.b.z;
  const z = h.a.y + OVERLAY.liftM;
  out.add(ax, ay, z, bx, by, h.b.y + OVERLAY.liftM, c);
  const k = Math.max(0.12, h.radius * 0.6);
  for (const [x, y] of [[ax, ay], [bx, by]] as const) {
    out.add(x - k, y, z, x + k, y, z, c);
    out.add(x, y - k, z, x, y + k, z, c);
  }
  if (Math.abs(ax - bx) + Math.abs(ay - by) < 1e-3) circle(out, ax, ay, h.radius, z, c);
}

/** A sector of an annulus (sim angles a0..a1, radii r0..r1) at height z. */
function sector(out: Lines, r0: number, r1: number, a0: number, a1: number, z: number, color: Color): void {
  const n = 12;
  for (let i = 0; i < n; i++) {
    const t0 = a0 + ((a1 - a0) * i) / n;
    const t1 = a0 + ((a1 - a0) * (i + 1)) / n;
    for (const r of [r0, r1]) out.add(Math.cos(t0) * r, Math.sin(t0) * r, z, Math.cos(t1) * r, Math.sin(t1) * r, z, color);
  }
  for (const a of [a0, a1]) out.add(Math.cos(a) * r0, Math.sin(a) * r0, z, Math.cos(a) * r1, Math.sin(a) * r1, z, color);
}

/** Non-solid hazards as zones, from the replicated map state and the hazard pose. */
function drawZones(out: Lines, view: FrameView): void {
  const z = OVERLAY.liftM;
  const hz = view.hazard;
  const m = view.map;
  if (view.mapId === MapId.CAKE) {
    CANDLE_TILES.forEach((t, i) => {
      if ((hz.candleFlicker[i] ?? 0) > 0 || (hz.candleFlame[i] ?? 0) > 0) circle(out, CAKE_GRID.cx[t], CAKE_GRID.cy[t], CAKE.candle.flameRadius, z, COLORS.zone);
    });
    for (let i = 0; i < CAKE_GRID.count; i++) {
      if (m.tileState[i] !== TileState.CRACKING) continue;
      const r = CAKE_GRID.R * 0.9;
      for (let k = 0; k < 6; k++) {
        const a0 = (k * Math.PI) / 3;
        const a1 = ((k + 1) * Math.PI) / 3;
        out.add(CAKE_GRID.cx[i] + Math.cos(a0) * r, CAKE_GRID.cy[i] + Math.sin(a0) * r, z, CAKE_GRID.cx[i] + Math.cos(a1) * r, CAKE_GRID.cy[i] + Math.sin(a1) * r, z, COLORS.zone);
      }
    }
  } else if (view.mapId === MapId.SPIN) {
    for (const b of hz.bubbles) {
      if (b.scale <= 0.05) continue;
      circle(out, b.x, b.y, SPIN.bubble.radius * b.scale, 0.75, COLORS.zone);
      if (b.wobble > 0) circle(out, b.x, b.y, SPIN.bubble.popRadius, z, COLORS.zone);
    }
  } else if (view.mapId === MapId.TEACUP) {
    const step = (Math.PI * 2) / TEACUP.rim.count;
    for (let k = 0; k < TEACUP.rim.count; k++) {
      const bit = 1 << k;
      const gone = (m.wedgeFallen & bit) !== 0;
      if (!gone && (m.wedgeWarn & bit) === 0) continue;
      sector(out, TEACUP.rim.inner, TEACUP.saucerRadius, k * step - step / 2, k * step + step / 2, z, gone ? COLORS.gone : COLORS.zone);
    }
  }
}

/** Everything the traced boundary depends on, as one comparable string. */
function boundaryKey(view: FrameView): string {
  const m = view.map;
  const q = OVERLAY.tiltStep;
  return `${Math.round(m.tiltX / q)},${Math.round(m.tiltY / q)},${m.wedgeFallen},${m.sdCursor},${m.tileState.join('')}`;
}

export function createSolidsOverlay(): SolidsOverlay {
  const root = new Group();
  root.name = 'solids-overlay';
  const material = new LineBasicMaterial({ vertexColors: true, depthTest: false, depthWrite: false, transparent: true, toneMapped: false });
  const boundary = new Lines();
  const solids = new Lines();
  for (const lines of [boundary, solids]) {
    const mesh = new LineSegments(lines.geometry, material);
    mesh.renderOrder = OVERLAY.renderOrder;
    mesh.frustumCulled = false;
    root.add(mesh);
  }
  const scratch: Solid[] = [];
  let probe: MapProbe | null = null;
  let traced = '';
  let source: ((view: FrameView, out: Solid[]) => number) | null = null;
  let rendered: (() => readonly RenderedHazard[]) | null = null;

  return {
    root,
    setRound(p) {
      probe = p;
      traced = '';
    },
    update(view) {
      if (!probe) return;
      const key = boundaryKey(view);
      if (key !== traced) {
        traced = key;
        boundary.reset();
        traceBoundary(probe, boundary);
        boundary.commit();
      }
      const n = source ? source(view, scratch) : probe.def.solids(probe.world, scratch);
      solids.reset();
      for (let i = 0; i < n; i++) drawSolid(solids, scratch[i]);
      drawZones(solids, view);
      // Default: the hazards of the map view presented last (the `?debug=solids` overlay of the renderer).
      const drawn = rendered ? rendered() : mapDebug.active?.hazards() ?? [];
      for (const h of drawn) drawRendered(solids, h);
      solids.commit();
    },
    setSource(fn) {
      source = fn;
    },
    setRendered(fn) {
      rendered = fn;
    },
    dispose() {
      boundary.geometry.dispose();
      solids.geometry.dispose();
      material.dispose();
    },
  };
}
